// Site pins: clustered when zoomed out, category-colored, with name labels at street zoom.
// Visited and selected states use feature-state (keyed by slug via promoteId).

const SRC = 'sites';
const PAPER = '#fbf6ea';
const INK = '#2b2418';

export const PIN_LAYERS = ['site-hit', 'site-pins'];

export function addSiteLayers(map, collection, featureCollection) {
  const colorMatch = ['match', ['get', 'category'],
    ...collection.categories.flatMap((c) => [c.slug, c.color]), '#6b5a43'];

  map.addSource(SRC, {
    type: 'geojson',
    data: featureCollection,
    cluster: true,
    clusterMaxZoom: 15,
    clusterRadius: 40,
    promoteId: 'slug',
  });

  map.addLayer({
    id: 'site-clusters', type: 'circle', source: SRC, filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#7a3e12',
      'circle-opacity': 0.92,
      'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 15, 25],
      'circle-stroke-color': PAPER,
      'circle-stroke-width': 3,
    },
  });
  map.addLayer({
    id: 'site-cluster-count', type: 'symbol', source: SRC, filter: ['has', 'point_count'],
    layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Medium'], 'text-size': 14, 'text-allow-overlap': true },
    paint: { 'text-color': PAPER },
  });

  const single = ['!', ['has', 'point_count']];
  // Invisible, larger circle so pins are easy to tap with a thumb.
  map.addLayer({
    id: 'site-hit', type: 'circle', source: SRC, filter: single,
    paint: { 'circle-radius': 22, 'circle-color': '#000', 'circle-opacity': 0 },
  });
  map.addLayer({
    id: 'site-pins', type: 'circle', source: SRC, filter: single,
    paint: {
      'circle-color': colorMatch,
      'circle-radius': ['interpolate', ['linear'], ['zoom'],
        13, ['case', ['boolean', ['feature-state', 'selected'], false], 9, 6],
        17, ['case', ['boolean', ['feature-state', 'selected'], false], 14, 10]],
      'circle-stroke-color': ['case', ['boolean', ['feature-state', 'selected'], false], INK, PAPER],
      'circle-stroke-width': ['case', ['boolean', ['feature-state', 'selected'], false], 3.5, 2.5],
    },
  });
  // Visited: a small dark ring outside the pin.
  map.addLayer({
    id: 'site-visited', type: 'circle', source: SRC, filter: single,
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 9, 17, 14],
      'circle-stroke-color': INK,
      'circle-stroke-width': 1.5,
      'circle-stroke-opacity': ['case', ['boolean', ['feature-state', 'visited'], false], 0.9, 0],
    },
  });
  map.addLayer({
    id: 'site-labels', type: 'symbol', source: SRC, filter: single, minzoom: 16,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Medium'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 16, 12.5, 18, 14.5],
      'text-anchor': 'top',
      'text-offset': [0, 1.1],
      'text-max-width': 9,
      'text-optional': true,
    },
    paint: { 'text-color': INK, 'text-halo-color': '#f7efdd', 'text-halo-width': 2 },
  });
}

export function setSiteData(map, featureCollection) {
  map.getSource(SRC)?.setData(featureCollection);
}

export function setSelected(map, prevSlug, slug) {
  if (!map.getSource(SRC)) return; // not loaded yet; re-applied after load
  if (prevSlug) map.setFeatureState({ source: SRC, id: prevSlug }, { selected: false });
  if (slug) map.setFeatureState({ source: SRC, id: slug }, { selected: true });
}

export function setVisited(map, slugs) {
  if (!map.getSource(SRC)) return;
  for (const s of slugs) map.setFeatureState({ source: SRC, id: s }, { visited: true });
}

export function onSiteTap(map, handler) {
  for (const layer of ['site-hit', 'site-pins']) {
    map.on('click', layer, (e) => {
      const f = e.features?.[0];
      if (f) handler(f.properties.slug);
    });
    map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
  }
  map.on('click', 'site-clusters', async (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const zoom = await map.getSource(SRC).getClusterExpansionZoom(f.properties.cluster_id);
    map.easeTo({ center: f.geometry.coordinates, zoom: Math.max(zoom, 16) });
  });
}

// Dashed "as the crow flies" line from the person to the selected site.
export function addPreviewLine(map) {
  map.addSource('preview', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'preview-line', type: 'line', source: 'preview',
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': '#7a3e12', 'line-width': 3, 'line-dasharray': [0.1, 2], 'line-opacity': 0.85 },
  }, 'site-clusters');
}

export function setPreviewLine(map, from, to) {
  const data = from && to
    ? { type: 'Feature', geometry: { type: 'LineString', coordinates: [[from.lon, from.lat], [to.lon, to.lat]] } }
    : { type: 'FeatureCollection', features: [] };
  map.getSource('preview')?.setData(data);
}
